/**
 * APIManager: WEHAGO 화상회의 API 호출 클래스
 * 원본: wehago-conference/src/utils/apiV2/index.js
 *
 * 인증/비인증 폴백 패턴 유지.
 * 401 응답 시 비인증(api0.wehago.com) 엔드포인트로 재시도.
 */
import axios, { type AxiosError } from 'axios';
import { apiCore as apiCoreInstance } from './apiCore';
import apiConfig from './config';
import { getRequestHeadersChange, getDocumentDetailRequestHeaders } from './signHeader';
import { getCookie } from '@/utils/cookie';

// ─── 타입 (공유 타입 파일에서 re-export) ──────────────────────────────────
export type {
  RoomAccessUser,
  RoomRecordData,
  CreatorFullPath,
  CreatorInfo,
  RoomInfo,
  UserInfo,
  MaterialFileInfo,
  MaterialListItem,
  SketchData,
  CreateSketchResult,
  ConnectingUser,
  UserInfoListItem,
  RoomDetailCreatorInfo,
  RoomDetailLinkedRoomInfo,
  RoomDetailInfo,
  RoomDetailParticipant,
  RoomDetailAttachment,
  RoomDetailResponse,
  SttHistoryItem,
  SttHistoryResponse,
} from '@/types/api';

import type {
  RoomInfo,
  UserInfo,
  ConnectingUser,
  UserInfoListItem,
  RoomDetailResponse,
  MaterialListItem,
  MaterialFileInfo,
  SketchData,
  CreateSketchResult,
  SttHistoryResponse,
  SttStatsResponse,
} from '@/types/api';

// ─── 유틸 ──────────────────────────────────────────────────────────────────

const serialize = (obj: Record<string, unknown>): string =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

/** basePath에 쿼리스트링이 있을 때만 ?qs를 붙인다 (빈 ? 방지) */
const buildPathUrl = (basePath: string, params: URLSearchParams): string => {
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

// ─── API 매니저 ────────────────────────────────────────────────────────────

class APIManager {
  private _apiCore = apiCoreInstance;
  private readonly _baseGuestUrl = apiConfig.baseGuestUrl;
  private readonly _baseApiUrl = apiConfig.baseApiUrl;
  private readonly _baseMeetApiUrl = apiConfig.weMeetAPI.baseApiUrl;
  private readonly _baseVideoConferenceUrl = `${apiConfig.weMeetAPI.baseApiUrl}${apiConfig.weMeetAPI.endpoint}`;
  private readonly _baseProductApiUrl = apiConfig.baseProductApiRootUrl;

  get selectedCno(): string | null {
    return this._apiCore.hSelectedCno;
  }

  get selectedCCode(): string | null {
    return this._apiCore.hSelectedCCode;
  }

  get token(): string | null {
    return this._apiCore.authAToken;
  }

  get locale(): string {
    return getCookie('locale') || 'ko';
  }

  // ─── 비인증 폴백 핸들러 ────────────────────────────────────────────────

  private handleErrorRequestUnAuthData = async (
    error: AxiosError,
    requestData: { method: string; pathUrl: string; data?: unknown }
  ) => {
    const isUnAuth =
      (error.response && error.response.status === 401) ||
      (error.response && error.response.data && (error.response.data as any).errors?.code === 'E002');

    if (isUnAuth) {
      const { method, pathUrl, data } = requestData;
      const guestUrl = `${this._baseGuestUrl}${pathUrl}`;
      const signature = await this._apiCore.getUnAuthSignature(pathUrl);
      return axios({ method, url: guestUrl, headers: { signature }, data });
    }
    throw error;
  };

  // ─── 화상대화방 ────────────────────────────────────────────────────────

  /** 구버전 화상대화방 정보 */
  getConferenceRoomInfo = (roomId: string): Promise<RoomInfo> => {
    const params = serialize({ video_chat_id: roomId, _t: Date.now() });
    const url = `${this._baseApiUrl}videoChat?${params}`;
    const headers = this._apiCore.getRequestHeaders(url);

    return axios({ method: 'GET', url, headers })
      .catch(async () => {
        const pathUrl = `/communication/rtc/videoChat?${params}`;
        const guestUrl = `${this._baseGuestUrl}${pathUrl}`;
        const signature = await this._apiCore.getUnAuthSignature(pathUrl);
        return axios({ method: 'GET', url: guestUrl, headers: { signature } });
      })
      .then((response) => {
        let serverDateHeader = response.headers && (response.headers.date || response.headers['date']);
        if (!serverDateHeader && response.headers && typeof (response.headers as any).get === 'function') {
          serverDateHeader = (response.headers as any).get('date');
        }
        if (serverDateHeader) {
          const serverTime = new Date(serverDateHeader as string).getTime();
          const clientTime = Date.now();
          (window as any).serverTimeOffset = serverTime - clientTime;
          console.log('[API] Server time offset calculated (Conference):', (window as any).serverTimeOffset, 'ms');
        } else {
          console.warn('[API] Could not find Date header in getConferenceRoomInfo response. serverTimeOffset remains 0.');
        }
        const { resultCode, resultData } = response.data;
        if (resultCode === 200 && resultData) return resultData as RoomInfo;
        throw new Error('getConferenceRoomInfo failed');
      });
  };

  /** 신버전 화상대화방 정보 */
  getVideoRoomInfo = (roomId: string): Promise<RoomInfo> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', roomId);
    url.searchParams.set('is_limit_time_check', 'true');
    url.searchParams.set('_t', String(Date.now()));
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ method, url: url.toString(), headers })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then((response) => {
        const { resultCode, resultData } = response.data;
        const info = resultData as RoomInfo | null;
        
        let serverTime = info && (info as any).server_time;
        if (serverTime) {
          const clientTime = Date.now();
          (window as any).serverTimeOffset = serverTime - clientTime;
          console.log('[API] Server time offset calculated via JSON Body (VideoRoom):', (window as any).serverTimeOffset, 'ms');
        } else {
          let serverDateHeader = response.headers && (response.headers.date || response.headers['date']);
          if (!serverDateHeader && response.headers && typeof (response.headers as any).get === 'function') {
            serverDateHeader = (response.headers as any).get('date');
          }
          if (serverDateHeader) {
            const parsedTime = new Date(serverDateHeader as string).getTime();
            const clientTime = Date.now();
            (window as any).serverTimeOffset = parsedTime - clientTime;
            console.log('[API] Server time offset calculated via Date Header (VideoRoom):', (window as any).serverTimeOffset, 'ms');
          } else {
            console.warn('[API] Could not find server_time or Date header in getVideoRoomInfo response. serverTimeOffset remains 0.');
          }
        }
        return info as RoomInfo;
      });
  };

  /** 방 정보 업데이트 */
  updateRoomInfo = (room: string, data: Record<string, unknown>): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/room`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());
    return axios({ headers, method: 'PUT', url: url.toString(), data })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 회의방 상세조회 (v2) */
  getRoomDetail = (roomId: string): Promise<RoomDetailResponse> => {
    const method = 'GET';
    const url = `${this._baseVideoConferenceUrl}/v2/room/detail/${encodeURIComponent(roomId)}`;
    const headers = {
      ...this._apiCore.getRequestHeaders(url),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    return axios({ method, url, headers })
      .then(({ data: { resultData } }) => resultData as RoomDetailResponse);
  };

  /** 방 공개여부 조회 */
  getRoomIsPublic = (room: string): Promise<boolean> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/is-public`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ method, url: url.toString(), headers })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as boolean);
  };

  /** 방 만료 시간 조회 */
  roomExpireTime = (roomId: string): Promise<unknown> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/expire-time`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', roomId);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ method, url: url.toString(), headers })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData;
        throw new Error('roomExpireTime failed');
      });
  };

  // ─── 사용자 ────────────────────────────────────────────────────────────

  /** 유저 정보 조회 (토큰 유효성 확인 포함) */
  getUserInfo = (): Promise<{ resultCode: number; resultMsg: string; resultData: UserInfo }> => {
    if (!this.token) return Promise.reject(new Error('no token'));
    const params = serialize({ cno: this.selectedCno });
    const url = `${this._baseVideoConferenceUrl}/user/userinfo?${params}`;
    const headers = this._apiCore.getRequestHeaders(url);

    return axios({ method: 'GET', url, headers }).then(
      ({ data: { resultCode, resultMsg, resultData } }) => {
        if (resultData && resultData.is_view_user_email === 'F') {
          resultData.user_email = '';
          resultData.user_default_email = '';
          resultData.wehago_email = '';
        }
        return { resultCode, resultMsg, resultData };
      }
    );
  };

  /** 기본 닉네임 조회 */
  getDefaultNickname = (room: string): Promise<string> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/user/default-name`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ method, url: url.toString(), headers })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as string);
  };

  /**
   * 직원정보 리스트 조회 (POST /user/userinfo/list)
   */
  getUserInfoList = async (portalIds: string[]): Promise<UserInfoListItem[]> => {
    if (portalIds.length === 0) return [];

    const params = serialize({ cno: this.selectedCno, portal_id: portalIds.join(',') });
    const url = `${this._baseVideoConferenceUrl}/user/userinfo/list?${params}`;
    const headers = this._apiCore.getRequestHeaders(url);

    const { data: { resultCode, resultData } } = await axios({ method: 'POST', url, headers });
    if (resultCode !== 200 || !Array.isArray(resultData)) return [];
    return resultData as UserInfoListItem[];
  };

  /** 접속 중인 유저 목록 (인증) */
  getConnectingUserAuth = (roomId: string): Promise<ConnectingUser[]> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/connecting-user2`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', roomId);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ method, url: url.toString(), headers })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as ConnectingUser[]);
  };

  /** 접속 중인 유저 목록 (비인증) */
  getConnectingUserUnAuth = async (roomId: string): Promise<ConnectingUser[]> => {
    const subPath = '/room/connecting-user2';
    const params = serialize({ room: roomId });
    const url = `${apiConfig.baseGuestVideoConferenceUrl}${subPath}?${params}`;
    const signaturePath = `/${apiConfig.guestEndpoint}${subPath}?${params}`;
    const tokenBase = new URL(apiConfig.baseGuestVideoConferenceUrl).origin;
    const signature = await this._apiCore.getUnAuthSignature(signaturePath, tokenBase);
    const { data: { resultData } } = await axios({
      headers: { signature },
      method: 'GET',
      url,
    });
    return resultData as ConnectingUser[];
  };

  // ─── 토큰 ──────────────────────────────────────────────────────────────

  /** Jitsi 서버 접속 토큰 요청 */
  requestRoomToken = (props: {
    roomId: string;
    emailToken?: string;
    joincode?: string;
    username?: string;
    user?: string;
  }): Promise<string> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/token`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);

    const data = {
      room: props.roomId,
      token: props.emailToken,
      joincode: props.joincode,
      username: props.username,
      user: props.user,
    };

    let headers = this._apiCore.getRequestHeaders(url.toString());
    if (props.emailToken) {
      headers = {};
      url.searchParams.delete('cno');
    }

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData as string);
  };

  /** 접속 코드 조회 */
  getRoomJoincode = (token: string): Promise<string> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/joincode`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as string);
  };

  // ─── 참여자 등록 ───────────────────────────────────────────────────────

  /** 참가자 등록 */
  insertUser = (data: {
    videoseq?: string;
    token?: string;
    username?: string;
    uid?: string;
    [key: string]: unknown;
  }): Promise<unknown> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/connect`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 초대 ──────────────────────────────────────────────────────────────

  /** 초대 목록 조회 */
  selectInviteList = (room: string): Promise<unknown[]> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/invite`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    url.searchParams.set('locale', this.locale);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'GET', url: url.toString() })
      .then(({ data: { resultData } }) => resultData as unknown[]);
  };

  /** 초대 요청 */
  requestInviteUser = (
    room: string,
    target: unknown,
    requestUser: string,
    requestUserName: string,
    inviteMessage: string
  ): Promise<unknown> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/invite`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    url.searchParams.set('locale', this.locale);

    const companyNameEl = document.getElementById('h_selected_company_name_kr') as HTMLInputElement | null;
    const data = {
      room,
      request_user: requestUser || 'null',
      request_user_name: requestUserName,
      user_list: target,
      invite_message: inviteMessage,
      company_name: companyNameEl?.value,
    };

    const headers = this._apiCore.getRequestHeaders(url.toString());
    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  /** 초대 수락 */
  accessInviteUser = (room: string, target: string): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/invite/access`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString(), data: { target } })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 초대 거부 */
  deniedInviteUser = (room: string, target: string): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/invite/denied`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString(), data: { target } })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 초대 전체 수락 */
  accessInviteListAll = (room: string): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/invite/access/all`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString() })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 초대 전체 거부 */
  deniedInviteListAll = (room: string): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/invite/denied/all`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString() })
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 마스터(호스트) 관리 ───────────────────────────────────────────────

  /** 마스터 제어 유저 조회 */
  getMasterControlUser = (room: string): Promise<unknown> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/master/control/user`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  /** 마스터 제어 유저 설정 */
  updateMasterControlUser = (
    token: string,
    instance: { videoseq?: string; audioActive?: boolean }
  ): Promise<unknown> => {
    const url = new URL(`${this._baseVideoConferenceUrl}/room/master/control/user`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const data: Record<string, unknown> = {};
    if (instance.videoseq !== undefined) data['videoseq'] = instance.videoseq;
    if (instance.audioActive !== undefined) data['audio_active'] = instance.audioActive;
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'PUT', url: url.toString(), data })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 마스터 유저 목록 조회 */
  getMasterUsers = (token: string): Promise<unknown[]> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/masters`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as unknown[]);
  };

  /** 접속 중인 마스터 유저 조회 */
  getRemainingMaster = (room: string): Promise<unknown[]> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/master/remaining-master`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as unknown[]);
  };

  /** 마스터 권한 부여 */
  grantMaster = (token: string, users: unknown[]): Promise<unknown> => {
    const method = 'PUT';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/master`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const data = { master: users };
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  /** 마스터 권한 해제 */
  clearMaster = (token: string, users: unknown[]): Promise<unknown> => {
    const method = 'DELETE';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/master`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const data = { unmaster: users };
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 강퇴 ──────────────────────────────────────────────────────────────

  /**
   * 강퇴 로그 적재
   * 강퇴 처리자 정보와 회의방 ID는 서버가 회의 토큰에서 채우므로 전송하지 않는다.
   */
  addKickedUser = (
    token: string,
    target: {
      videoseq: string;
      user_id?: string;
      user_name?: string;
      user_type?: number;
    }
  ): Promise<unknown> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/room/kick-user/jwt`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    const data = target;
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: `${basePath}?${url.searchParams.toString()}`,
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 녹화 ──────────────────────────────────────────────────────────────

  /** 녹화 신청 */
  recordRequest = (room: string, requestUser: string): Promise<unknown> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/record/request`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const data = { room, request_user: requestUser };
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then((res) => res.data);
  };

  /** 녹화 데이터 수정 */
  updateRecordData = (room: string, data: Record<string, unknown>): Promise<unknown> => {
    const method = 'PATCH';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/record`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData;
        throw new Error('updateRecordData failed');
      });
  };

  /** 녹화 중인 정보 조회 */
  findRecordingInfo = (room: string): Promise<unknown> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/record/recording`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData;
        throw new Error('findRecordingInfo failed');
      });
  };

  /** 녹화 횟수 조회 */
  recordedCount = (token: string): Promise<number> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/record/count`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('jwt', token);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData as number;
        throw new Error('recordedCount failed');
      });
  };

  // ─── 자막/번역 ─────────────────────────────────────────────────────────

  /** 자막 사용여부 조회 */
  getUseCaption = (room: string): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/caption/use-caption`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, url: url.toString() })
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData;
        throw new Error('getUseCaption failed');
      });
  };

  /** 자막 사용여부 변경 */
  updateUseCaption = (jwt: string, useCaption: boolean): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/caption/use-caption`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('jwt', jwt);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, url: url.toString(), method: 'PUT', data: { use_caption: useCaption } })
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData;
        throw new Error('updateUseCaption failed');
      });
  };

  /** 진행 중 회의 STT/자막 대화 기록 조회 (회원 전용) */
  getSttHistory = async (roomId: string, options?: { lastTime?: string | number }): Promise<SttHistoryResponse> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/chat/stt/history`,
    );
    url.searchParams.set('roomId', roomId);
    if (options?.lastTime != null) {
      url.searchParams.set('lastTime', String(options.lastTime));
    }

    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData as SttHistoryResponse;
    throw new Error(data.resultMsg || 'getSttHistory failed');
  };

  /** STT 통계 조회 — AI 요약 알림 배너 노출 조건 판정용 (회원 전용) */
  getSttStats = async (roomId: string): Promise<SttStatsResponse> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/chat/stt/stats`,
    );
    url.searchParams.set('roomId', roomId);

    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData as SttStatsResponse;
    throw new Error(data.resultMsg || 'getSttStats failed');
  };

  // ─── 채팅 저장 ─────────────────────────────────────────────────────────

  /** 채팅 내역 저장 */
  saveChatting = (data: Record<string, unknown>) => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/history/chatting`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const headers = this._apiCore.getRequestHeaders(url.toString());
    return axios({ headers, method: 'POST', url: url.toString(), data });
  };

  /** 음성 텍스트(자막) 저장 (인증) */
  saveChattingMessage = (data: Record<string, unknown>) => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/history/chattingMessage`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const headers = this._apiCore.getRequestHeaders(url.toString());
    return axios({ headers, method: 'POST', url: url.toString(), data });
  };

  /** 음성 텍스트(자막) 저장 (비인증) */
  saveChattingMessage2 = async (data: Record<string, unknown>) => {
    const subPath = '/history/chattingMessage';
    const guestUrl = `${apiConfig.baseGuestVideoConferenceUrl}${subPath}`;
    const signaturePath = `/${apiConfig.guestEndpoint}${subPath}`;
    const tokenBase = new URL(apiConfig.baseGuestVideoConferenceUrl).origin;
    const signature = await this._apiCore.getUnAuthSignature(signaturePath, tokenBase);
    return axios({ headers: { signature }, method: 'POST', url: guestUrl, data });
  };

  // ─── 가상배경 이미지 ───────────────────────────────────────────────────

  /** 가상배경 이미지 저장 */
  insertBackgroundData = (param: Record<string, unknown>): Promise<unknown> => {
    const method = 'PUT';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/user/file_uuid`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data: param })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data: param,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  /** 가상배경 이미지 목록 조회 */
  selectBackgroundData = (portalId: string): Promise<unknown[]> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/user/file_select`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('portal_id', portalId);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString() })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        })
      )
      .then(({ data: { resultData } }) => resultData as unknown[]);
  };

  /** 가상배경 이미지 삭제 */
  deleteBackgroundData = (roomId: string, portalId: string, deleteUuid: string): Promise<unknown> => {
    const method = 'DELETE';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/user/file_delete`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const data = { roomId, portal_id: portalId, file_uuid: deleteUuid };
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 서비스 ────────────────────────────────────────────────────────────

  /** 서비스 배포 여부 조회 */
  getIsServiceDeploy = (serviceCode: string): Promise<{ isServiceDeploy: string }> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/service/is-service-deploy`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('service_code', serviceCode);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, url: url.toString(), method: 'GET' })
      .then(({ data: { resultCode, resultData } }) => {
        if (resultCode === 200) return resultData as { isServiceDeploy: string };
        throw new Error('getIsServiceDeploy failed');
      });
  };

  /** 화상회의 서비스 키 조회 */
  getVideoServiceKey = (): Promise<{ service_key: string; service_code: string }> => {
    const method = 'GET';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/service/secretkey`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    console.log('[getVideoServiceKey] Fetching secretkey from URL:', url.toString());

    return axios({ headers, method, url: url.toString() })
      .then((response) => {
        console.log('[getVideoServiceKey] Success response:', response.data);
        if (response.data && response.data.errors && response.data.errors.code === 'E002') {
          console.log('[getVideoServiceKey] E002 detected in 200 OK. Forcing unAuth retry.');
          const error: any = new Error('Server Wehago Session is not exist');
          error.response = response;
          throw error;
        }
        return response;
      })
      .catch((err: AxiosError) => {
        console.warn('[getVideoServiceKey] Catch block triggered:', err);
        return this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
        });
      })
      .then((res: any) => {
        console.log('[getVideoServiceKey] Final response before mapping resultData:', res?.data);
        if (!res?.data?.resultData) {
          throw new Error('getVideoServiceKey resultData is undefined. response data: ' + JSON.stringify(res?.data));
        }
        return res.data.resultData as { service_key: string; service_code: string };
      });
  };

  // ─── 로그 ──────────────────────────────────────────────────────────────

  /** 시스템 로그 전송 */
  sendLogSystem = (data: Record<string, unknown>): Promise<unknown> => {
    const method = 'POST';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/logsystem/post`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then((res) => res.data);
  };

  // ─── 대기방 ────────────────────────────────────────────────────────────

  /** 대기방 유저 상태 등록 */
  setReadyUserStatus = (room: string, token: string, status: string): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/ready/users`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    url.searchParams.set('jwt', token);
    url.searchParams.set('user_status', status);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString() })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 대기방 유저 입장 처리 */
  setReadyEnterUser = (room: string, token: string, status: string): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/ready/usersEnter`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    url.searchParams.set('jwt', token);
    url.searchParams.set('user_status', status);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'POST', url: url.toString() })
      .then(({ data: { resultData } }) => resultData);
  };

  /** 대기방 유저 삭제 */
  deleteReadyUser = (room: string, token: string, portalId: string): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/ready/users`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    url.searchParams.set('jwt', token);
    url.searchParams.set('portal_id', portalId);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method: 'DELETE', url: url.toString() })
      .then(({ data: { resultData } }) => resultData);
  };

  /**
   * 대기방 유저 삭제 (페이지 언로드 전용)
   */
  deleteReadyUserOnUnload = (room: string, token: string, portalId: string): void => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/ready/users`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('room', room);
    url.searchParams.set('jwt', token);
    url.searchParams.set('portal_id', portalId);

    const signedHeaders = this._apiCore.getRequestHeaders(url.toString());
    const headers: Record<string, string> = {};
    Object.entries(signedHeaders).forEach(([key, value]) => { headers[key] = String(value); });

    try {
      void fetch(url.toString(), { method: 'DELETE', headers, keepalive: true });
    } catch {
      /* 언로드 중 실패는 서버 스케줄러(ClearReadyOnlyRoom)가 정리한다 */
    }
  };

  /** 대기방 마스터 준비 설정 */
  readyGrantMaster = (
    token: string,
    users: unknown[],
    status: string,
    userNo: string
  ): Promise<unknown> => {
    const method = 'PUT';
    const basePath = `${apiConfig.weMeetAPI.endpoint}/ready`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('jwt', token);
    url.searchParams.set('user_status', status);
    url.searchParams.set('user_no', userNo);
    const data = { master: users };
    const headers = this._apiCore.getRequestHeaders(url.toString());

    return axios({ headers, method, url: url.toString(), data })
      .catch((err: AxiosError) =>
        this.handleErrorRequestUnAuthData(err, {
          method,
          pathUrl: buildPathUrl(basePath, url.searchParams),
          data,
        })
      )
      .then(({ data: { resultData } }) => resultData);
  };

  // ─── 위톡 ──────────────────────────────────────────────────────────────

  /** 위톡 대화방 정보 조회 */
  getWeTalkRoomInfo = (roomId: string): Promise<unknown> => {
    const params = serialize({ room_id: roomId, cno: this.selectedCno });
    const url = `${new URL(apiConfig.baseApiRootUrl).origin}/communication/we-talk/room-detail?${params}`;
    const headers = this._apiCore.getRequestHeaders(url);

    return axios({ method: 'GET', url, headers })
      .then(({ data: { resultData, resultCode } }) => {
        if (resultCode === 200) return resultData.roomDetail;
        throw new Error('getWeTalkRoomInfo failed');
      });
  };

  // ─── WeDrive 문서공유 ──────────────────────────────────────────────────

  /** WeDrive 초기 정보 조회 */
  getWedriveInitInfo = async (): Promise<{ resultCode: number; resultData?: Record<string, unknown>; error?: unknown }> => {
    const cno = this._apiCore.hSelectedCno;
    const ccode = this._apiCore.hSelectedCCode;
    const url = `${this._baseProductApiUrl}common/wedrive/init-info?cno=${cno}&ccode=${ccode}`;
    const headers: Record<string, string | number | boolean> = { ...getRequestHeadersChange(url), service: 'common' };
    try {
      const { data } = await axios({ method: 'GET', url, headers });
      return data;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status: number; data: unknown } };
      return { resultCode: axiosErr?.response?.status ?? 500, error: err };
    }
  };

  /** WeDrive 파일 목록 조회 */
  getWedriveFileList = async (data: Record<string, unknown>): Promise<{ resultCode: number; resultData?: unknown; error?: unknown }> => {
    const url = `${this._baseProductApiUrl}common/wedrive/get/file-list`;
    const headers: Record<string, string | number | boolean> = {
      ...getRequestHeadersChange(url),
      service: 'common',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    };
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    try {
      const { data: result } = await axios({ method: 'POST', url, headers, data: params.toString() });
      return result;
    } catch (err) {
      return { resultCode: 500, error: err };
    }
  };

  /** WeDrive 파일 검색 */
  getWedriveSearchKeyword = async (keyword: string, data: Record<string, unknown>): Promise<{ resultCode: number; resultData?: unknown; error?: unknown }> => {
    const url = `${this._baseProductApiUrl}common/wedrive/search`;
    const headers: Record<string, string | number | boolean> = {
      ...getRequestHeadersChange(url),
      service: 'common',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    };
    const merged = { ...data, searchText: keyword };
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(merged)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    try {
      const { data: result } = await axios({ method: 'POST', url, headers, data: params.toString() });
      return result;
    } catch (err) {
      return { resultCode: 500, error: err };
    }
  };

  /** WeDrive 잠금 폴더 비밀번호 확인 */
  getWedriveLockCheck = async (
    tokenId: string,
    password: string,
    fileUniqueKey: string,
  ): Promise<{ resultCode: string; serverMsg?: string }> => {
    const url = `${this._baseProductApiUrl}WeDriveStorage/services/csb`;
    const headers = { ...getRequestHeadersChange(url), method: 'checkLockPasswordRenewal', service: 'lockService' };
    try {
      const { data } = await axios({ method: 'POST', url, headers, data: { TokenID: tokenId, Password: password, FileUniqueKey: fileUniqueKey } });
      return data;
    } catch {
      return { resultCode: 'ERROR' };
    }
  };

  /** WeDrive 위하고 비밀번호 확인 (잠금 비밀번호 찾기 1단계) */
  getWedriveCheckLoginPassword = async (
    tokenId: string,
    password: string,
  ): Promise<{ resultCode: string; serverMsg?: string }> => {
    const url = `${this._baseProductApiUrl}WeDriveStorage/services/csb`;
    const headers = { ...getRequestHeadersChange(url), method: 'checkLoginPassword', service: 'lockService' };
    try {
      const { data } = await axios({ method: 'POST', url, headers, data: { TokenID: tokenId, Password: password } });
      return data;
    } catch {
      return { resultCode: 'ERROR' };
    }
  };

  /** WeDrive 잠금 비밀번호 이메일 발송 (잠금 비밀번호 찾기 2단계) */
  getWedriveGetLockFolderPassword = async (
    tokenId: string,
    fileUniqueKey: string,
    userId: string,
    userName: string,
  ): Promise<{ resultCode: string; resultList?: Array<{ email: string }>; serverMsg?: string }> => {
    const url = `${this._baseProductApiUrl}WeDriveStorage/services/csb`;
    const headers = { ...getRequestHeadersChange(url), method: 'getLockFolderPasswordRenewal', service: 'lockService' };
    try {
      const { data } = await axios({ method: 'POST', url, headers, data: { TokenID: tokenId, FileUniqueKey: fileUniqueKey, UserID: userId, UserName: userName } });
      return data;
    } catch {
      return { resultCode: 'ERROR' };
    }
  };

  // ─── 대기방(WaitingRoom) API ──────────────────────────────────────────

  /**
   * 롱폴링 — 마스터 승인/거부 응답 대기.
   * v1: apiV2/index.js lpevent()
   */
  lpevent = async (
    option: { room: string; userIdentify: string; profileUrl?: string; timeout?: number; jwt?: string },
    callback: (data: { extra_data: { type: string; access_key?: string; status?: string; user_identify?: string; user_name?: string; removeUserIdentifyList?: string[]; profile_url?: string } }) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    if (signal?.aborted) return;

    const basePath = `${apiConfig.weMeetAPI.endpoint}/lpevent`;
    const isProd = ['wehago', 'wehagov'].includes(import.meta.env.VITE_APP_TYPE ?? '');
    const isGuest = !this._apiCore.authAToken; // 포털 세션 토큰이 없으면 비회원(게스트)

    // 운영기(prod)이면서 비회원(게스트)일 때만 비인증 게이트웨이(api0)를 사용
    const baseUrl = isProd && isGuest ? this._baseGuestUrl : this._baseMeetApiUrl;
    const url = `${baseUrl}${basePath}`;

    const query = serialize({
      room: option.room,
      user_identify: option.userIdentify,
      profile_url: option.profileUrl ?? '',
      timeout: option.timeout ?? 30,
      jwt: option.jwt ?? '',
    });

    try {
      let headers: Record<string, string> = {};

      // 운영기 비회원은 비인증 API (api0) 서명이 필수
      if (isProd && isGuest) {
        const pathUrl = `${basePath}?${query}`;
        const tokenBase = new URL(baseUrl).origin;
        const signature = await this._apiCore.getUnAuthSignature(pathUrl, tokenBase);
        headers = { signature };
      } else {
        headers = this._apiCore.getRequestHeaders(`${url}?${query}`) as Record<string, string>;
      }

      if (signal?.aborted) return;

      const { data } = await axios({ method: 'POST', url: `${url}?${query}`, headers, signal });

      if (signal?.aborted) return;

      // 롱폴링 타임아웃(30초) 등으로 인해 빈 응답이 오면 다시 롱폴링 시작
      if (!data || !data.extra_data) {
        this.lpevent(option, callback, signal);
        return;
      }

      callback(data);
    } catch (err) {
      if (signal?.aborted) return;
      // 에러 발생 시 2초 후 재시도
      setTimeout(() => this.lpevent(option, callback, signal), 2000);
    }
  };

  /**
   * 참여 요청 보내기 (게스트 → 마스터).
   * v1: apiV2/index.js requestJoinRoom()
   */
  requestJoinRoom = async (props: {
    room: string;
    userIdentify: string;
    username: string;
    joincode?: string;
    profileUrl?: string;
    rankName?: string;
    isGuest?: boolean;
  }): Promise<unknown> => {
    const subPath = '/v2/standby/request/joinroom';
    const data = {
      room: props.room,
      user_identify: props.userIdentify,
      user_name: props.username,
      joincode: props.joincode ?? '',
      profile_url: props.profileUrl ?? '',
      rank_name: props.rankName ?? '',
    };

    if (props.isGuest) {
      const guestUrl = `${apiConfig.baseGuestVideoConferenceUrl}${subPath}`;
      const signaturePath = `/${apiConfig.guestEndpoint}${subPath}`;
      const tokenBase = new URL(apiConfig.baseGuestVideoConferenceUrl).origin;
      const signature = await this._apiCore.getUnAuthSignature(signaturePath, tokenBase);
      const { data: result } = await axios({ method: 'POST', url: guestUrl, headers: { signature }, data });
      return result?.resultData;
    }

    const basePath = `${apiConfig.weMeetAPI.endpoint}${subPath}`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data: result } = await axios({ method: 'POST', url: url.toString(), headers, data });
    return result?.resultData;
  };

  /**
   * 참여 요청 재시작 (게스트 → 마스터, is_restart=true).
   * v1: apiV2/index.js restartRequestJoinRoom()
   */
  restartRequestJoinRoom = async (props: {
    room: string;
    userIdentify: string;
    username: string;
    joincode?: string;
    profileUrl?: string;
    rankName?: string;
    isGuest?: boolean;
  }): Promise<unknown> => {
    const subPath = '/v2/standby/request/joinroom';
    const data = {
      room: props.room,
      user_identify: props.userIdentify,
      user_name: props.username,
      joincode: props.joincode ?? '',
      is_restart: true,
      profile_url: props.profileUrl ?? '',
      rank_name: props.rankName ?? '',
    };

    if (props.isGuest) {
      const guestUrl = `${apiConfig.baseGuestVideoConferenceUrl}${subPath}`;
      const signaturePath = `/${apiConfig.guestEndpoint}${subPath}`;
      const tokenBase = new URL(apiConfig.baseGuestVideoConferenceUrl).origin;
      const signature = await this._apiCore.getUnAuthSignature(signaturePath, tokenBase);
      const { data: result } = await axios({ method: 'POST', url: guestUrl, headers: { signature }, data });
      return result?.resultData;
    }

    const basePath = `${apiConfig.weMeetAPI.endpoint}${subPath}`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data: result } = await axios({ method: 'POST', url: url.toString(), headers, data });
    return result?.resultData;
  };

  /**
   * 대기 중인 참여 요청자 목록 조회 (마스터용).
   * v1: apiV2/index.js getRequestJoinUsers()
   */
  getRequestJoinUsers = async (token: string): Promise<{ user_identify: string; user_name: string; profile_url?: string }[]> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/standby/users`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    url.searchParams.set('jwt', token);

    try {
      const headers = this._apiCore.getRequestHeaders(url.toString());
      const { data: result } = await axios({ method: 'GET', url: url.toString(), headers });
      if (result?.resultCode === 200) return result.resultData ?? [];
      return [];
    } catch (error: any) {
      // 대기방에 유저가 없을 때 SequelizeEmptyResultError로 인해 404 "요청한 리소스가 없습니다."가 반환됨
      if (error.response && error.response.status === 404) {
        return [];
      }
      throw error;
    }
  };

  /**
   * 참여 요청 수락/거부 응답 (마스터 → 게스트).
   * v1: apiV2/index.js responseJoinRoom()
   */
  responseJoinRoom = async (props: {
    targetUsers: { user_identify: string }[];
    status: 'allow' | 'deny';
    token: string;
  }): Promise<unknown> => {
    const basePath = `${apiConfig.weMeetAPI.endpoint}/standby/response/joinroom`;
    const url = new URL(`${this._baseMeetApiUrl}${basePath}`);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const data = {
      target_users: props.targetUsers.map(u => u.user_identify),
      status: props.status,
      jwt: props.token,
    };

    const { data: result } = await axios({ method: 'POST', url: url.toString(), headers, data });
    if (result?.resultCode === 200) return result.resultData;
    throw new Error('responseJoinRoom failed');
  };

  /** 문서 상세 정보 조회 (이미지 변환) */
  getDocumentDetail = async (serializedData: string, method: string): Promise<{ status: number; data: { resultCode?: string; resultList?: unknown[] } }> => {
    const url = `${this._baseProductApiUrl}ObjectStorageCommon/services/common`;
    const headers = getDocumentDetailRequestHeaders(url, method);
    const result = await fetch(url, {
      headers: headers as HeadersInit,
      method: 'POST',
      body: serializedData,
    }).then(async (res) => {
      const data = await res.json();
      return { status: res.status, data };
    });
    return result;
  };

  /** 파일 확장자/mimeType에 따른 변환 메서드 결정 */
  getDocumentMethod = (extension: string, mimeType: string): string | false => {
    const pattern = /(hwp|doc|docx|rtf|xls|xlsx|cell|ppt|pptx|show|bmp|gif|jpg|jpeg|txt|one|png|pdf)$/i;
    if (!extension.match(pattern)) return false;
    if (mimeType === 'IMAGE') return 'getImageURL';
    return 'getOfficePreView';
  };

  // ─── 회의자료 (v2/material) ──────────────────────────────────────────────

  /** 회의자료 목록 조회 */
  getMaterialList = async (roomId: string): Promise<MaterialListItem[]> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/material`);
    url.searchParams.set('roomId', roomId);
    const cno = this.selectedCno;
    if (cno) url.searchParams.set('cno', cno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData as MaterialListItem[];
    throw new Error(data.resultMsg || 'getMaterialList failed');
  };

  /** 회의자료 등록 */
  createMaterial = async (
    roomId: string,
    materials: { fileName: string; fileInfo?: MaterialFileInfo }[],
  ): Promise<{ materialNos: number[] }> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/material`);
    const cno = this.selectedCno;
    if (cno) url.searchParams.set('cno', cno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data } = await axios.post(url.toString(), { roomId, materials }, { headers });
    if (data.resultCode === 200) return data.resultData as { materialNos: number[] };
    throw new Error(data.resultMsg || 'createMaterial failed');
  };

  /** 스케치 생성 */
  createSketch = async (
    roomId: string,
    sketchName: string,
    sketchData: SketchData,
    templateType?: string,
  ): Promise<CreateSketchResult> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/sketch`);
    const cno = this.selectedCno;
    if (cno) url.searchParams.set('cno', cno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const body: Record<string, unknown> = { roomId, sketchName, sketchData };
    if (templateType) body.templateType = templateType;

    const { data } = await axios.post(url.toString(), body, { headers });
    if (data.resultCode === 200) return data.resultData as CreateSketchResult;
    throw new Error(data.resultMsg || 'createSketch failed');
  };

  /** 회의자료 삭제 */
  deleteMaterial = async (materialNo: number): Promise<{ resultCode: number; resultMsg: string }> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/material/delete`);
    const cno = this.selectedCno;
    if (cno) url.searchParams.set('cno', cno);
    url.searchParams.set('materialNo', String(materialNo));
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data } = await axios.post(url.toString(), null, { headers });
    return { resultCode: data.resultCode, resultMsg: data.resultMsg };
  };

  // ─── 참여 회의 / 연결 회의 / 편집 정보 ──────────────────────────────────

  /** 참여한 회의 목록 조회 (GET /v2/room/participated) */
  getParticipatedRooms = async (props: {
    pageNumber: number;
    pageSize: number;
    keyword?: string;
  }): Promise<{
    roomList: {
      roomId: string;
      name: string;
      startDateTime: number | null;
      endDateTime: number | null;
      lastAccessedAt: number;
      participantCount: number;
      participantProfileUrlList: string[];
    }[];
  }> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/room/participated`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    url.searchParams.set('pageNumber', String(props.pageNumber));
    url.searchParams.set('pageSize', String(props.pageSize));
    if (props.keyword) url.searchParams.set('keyword', props.keyword);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData;
    throw new Error(data.resultMsg || 'getParticipatedRooms failed');
  };

  /** 연결된 회의(연계 정보) 변경 (PUT /v2/room/link) */
  updateLinkedRoom = async (props: {
    roomId: string;
    connectedRoomId?: string | null;
  }): Promise<void> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/room/link`);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = this._apiCore.getRequestHeaders(url.toString());

    const { data } = await axios.put(url.toString(), {
      roomId: props.roomId,
      connectedRoomId: props.connectedRoomId ?? null,
    }, { headers });
    if (data.resultCode !== 200) {
      throw new Error(data.resultMsg || 'updateLinkedRoom failed');
    }
  };

  /** 회의방 편집 정보 조회 (GET /v2/room/edit/:roomId) */
  getRoomEditInfo = async (roomId: string): Promise<{
    name: string;
    isReservation: boolean;
    reservationStartDateTime: number | null;
    durationMinutes: number | null;
    isPublic: boolean;
    securityType: string | null;
    joincode: string | null;
    aiTranslationType: string | null;
    calendarNo: number | null;
    linkedRoomInfo: {
      roomId: string;
      name: string | null;
      accessType: string;
      startDateTime: number | null;
      endDateTime: number | null;
    } | null;
    participantList: {
      type: string;
      companyNo?: number;
      userNo?: number;
      portalId?: string;
      isMaster: boolean;
      userName: string | null;
      email?: string;
      phone?: string;
      profileUrl: string | null;
    }[];
    attachmentList: {
      materialNo: number | null;
      newFileName: string;
      orgFileName: string;
      thumbnail: string;
      fileSize: number;
      bucketType: string;
    }[];
    topic: string | null;
    keywordList: string[];
  }> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/room/edit/${encodeURIComponent(roomId)}`,
    );
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData;
    throw new Error(data.resultMsg || 'getRoomEditInfo failed');
  };

  /** 회의방 수정 (PUT /v2/room/:roomId) */
  updateRoom = async (
    roomId: string,
    body: {
      name: string;
      isPublic?: boolean;
      securityType?: 'PASSWORD' | 'MASTER_APPROVAL' | null;
      joincode?: string | null;
      aiTranslationType?: 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null;
      linkedRoomInfo?: {
        roomId: string;
        name?: string | null;
        accessType: 'ALL' | 'PREVIOUS_ONLY';
        startDateTime?: number | null;
        endDateTime?: number | null;
      } | null;
      isReservation: boolean;
      reservationStartDateTime?: number;
      durationMinutes?: number;
      calendarNo?: number | null;
      participantList?: unknown[];
      attachmentList?: unknown[];
      topic?: string | null;
      keywordList?: string[] | null;
    },
  ): Promise<void> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/room/${encodeURIComponent(roomId)}`,
    );
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.put(url.toString(), body, { headers });
    if (data.resultCode !== 200) {
      throw new Error(data.resultMsg || 'updateRoom failed');
    }
  };

  // ─── 회의 기록 상세 (v2/record/detail) ──────────────────────────────────

  /** 회의 기록 상세 조회 — attendees는 실제 접속 이력 기반 */
  getRecordDetail = async (roomId: string): Promise<{
    attendees: {
      portalId: string;
      userName: string | null;
      profileUrl: string | null;
      userNo: number | null;
      cno: number | null;
      rankName: string | null;
      organizationPath: string | null;
    }[];
    [key: string]: unknown;
  }> => {
    const url = new URL(`${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/record/detail`);
    url.searchParams.set('roomId', roomId);
    if (this.selectedCno) url.searchParams.set('cno', this.selectedCno);
    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData;
    throw new Error(data.resultMsg || 'getRecordDetail failed');
  };

  // ─── 초대 참석자 (v2/invite/participant) ─────────────────────────────────

  /** 초대된 참석자 목록 조회 (GET /v2/invite/participant) */
  getInvitedParticipantList = async (roomId: string): Promise<{
    participantList: {
      accessUserNo: number;
      type: 'USER' | 'PHONE' | 'EMAIL';
      cno: number | null;
      userNo: number | null;
      portalId: string | null;
      phone: string | null;
      email: string | null;
      userName: string | null;
      isMaster: boolean;
      profileUrl: string | null;
    }[];
  }> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/invite/participant`,
    );
    url.searchParams.set('roomId', roomId);
    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.get(url.toString(), { headers });
    if (data.resultCode === 200) return data.resultData;
    throw new Error(data.resultMsg || 'getInvitedParticipantList failed');
  };

  /** 참여자 알림 재전송 (POST /v2/invite/participant/resend) */
  resendParticipantNotification = async (
    roomId: string,
    accessUserNoList: number[],
  ): Promise<void> => {
    const url = new URL(
      `${this._baseMeetApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/invite/participant/resend`,
    );
    url.searchParams.set('roomId', roomId);
    const headers = {
      ...this._apiCore.getRequestHeaders(url.toString()),
      ...(this.selectedCno ? { cno: this.selectedCno } : {}),
    };

    const { data } = await axios.post(url.toString(), { accessUserNoList }, { headers });
    if (data.resultCode !== 200) {
      throw new Error(data.resultMsg || 'resendParticipantNotification failed');
    }
  };
}

// ─── 싱글턴 인스턴스 ──────────────────────────────────────────────────────

const apiManager = new APIManager();
export default apiManager;
export { APIManager };
